import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import t from 'react-translate';
import { css } from '@emotion/react';
import { AngularServicesContext } from 'react-app';

// redux
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import { getCurrentCourse, getFlatCourseAliases } from 'redux/selectors/course';
import { getCanLinkCollectionLesson, canConvertToCollectionLecture, getCanConvertToCollectionLesson } from 'redux/actions/collections';

// hooks
import { useLecturePageFromParams, useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import { useCreateLesson, useDuplicateLecture, useDeleteLecture, useHideAddUIOnOpen } from 'lecture_pages/hooks/lecture-page-edit-top-panel';

// styles
import { white, hexToRgbaString } from 'styles/global_defaults/colors';
import { doubleSpacing } from 'styles/global_defaults/scaffolding';

// components
import NvDropdown, { NvDropdownAlign, NvDropdownButtonStyle, NvDropdownOption } from 'shared/components/inputs/nv-dropdown';
import AddToCollectionModal from 'courses/components/course-lesson-to-collection/add-to-collection-modal';
import { LecturePageMode } from 'lecture_pages/components';
import LinkCollectionLessonsModal from '../../../../courses/components/course-lesson-link/link-lessons-modal';
import { config } from '../../../../../config/config.json';


const MoreMenuActionButton = () => {
  const styles = css`
    .edit-menu-icon-styles {
      width: ${doubleSpacing}px;
      height: ${doubleSpacing}px;
      border-radius: 50%;
      background-color: ${hexToRgbaString(white, 0.2)};

      &:hover {
        background-color: ${hexToRgbaString(white, 0.4)};
        cursor: pointer;
      }
    }
  `;

  const params = useLecturePageParams();
  const dispatch = useAppDispatch();
  const { $state } = useContext(AngularServicesContext);
  const lecturePage = useLecturePageFromParams();
  const currentCourse = useSelector(getCurrentCourse);
  const courseAliases = useSelector(state => getFlatCourseAliases(state, params.catalogId));
  const { canConvertToCollectionLesson, canLessonContentConvertibleToCollection } = useSelector(state => state.app.collection);
  const { canLinkCollectionLesson } = useSelector(state => state.app.collection);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showAddFromLessonModal, setShowAddFromLessonModal] = useState(false);
  const [showAddToLessonModal, setShowAddToLessonModal] = useState(false);

  const duplicateThisLecture = useDuplicateLecture();
  const deleteThisLecture = useDeleteLecture();
  const createLesson = useCreateLesson();
  const hideAddUIOnOpen = useHideAddUIOnOpen();

  useEffect(() => {
    // Don't run if lecturePage hasn't loaded yet
    if (!lecturePage?.id) return;
    // Make these requests only within a course lesson.
    if (!currentCourse?.isContentManagementCollection) {
      /**
       * To display the 'Add to Lesson Library' option, check if the user has a
       * collection with at least one folder available to convert a lesson
       * from a course to the collection.
       */
      if (canConvertToCollectionLesson === null) {
        dispatch(getCanConvertToCollectionLesson());
      }

      // Check that the current lesson contains only components supported by the Lesson Library.
      const canConvertLesson = canLessonContentConvertibleToCollection?.[lecturePage?.id];
      if (
        !lecturePage?.isLinked
        && canConvertToCollectionLesson
        && (canConvertLesson === undefined || canConvertLesson === null)
      ) {
        dispatch(canConvertToCollectionLecture({
          id: lecturePage?.id,
        }));
      }
    }
  }, [
    currentCourse?.isContentManagementCollection, canConvertToCollectionLesson,
    canLessonContentConvertibleToCollection, dispatch, lecturePage?.id,
    lecturePage?.isLinked,
  ]);


  useEffect(() => {
    if (!currentCourse?.isContentManagementCollection) {
      /**
       * To display the 'Add from Lesson Library' option, check if the user has
       * any collection with at least one folder containing a lesson.
       */
      if (canLinkCollectionLesson === null) {
        dispatch(getCanLinkCollectionLesson());
      }
    }
  }, [
    dispatch, canLinkCollectionLesson,
    currentCourse?.isContentManagementCollection,
  ]);


  const outlineHref = () => $state.go(
    currentCourse.isProgram ? 'program-timelines-edit' : 'course-home-edit',
    { catalogId: params.catalogId },
  );

  const addFromLessonLibrary = useCallback(() => {
    setShowAddFromLessonModal(true);
  }, []);

  const closeAddFromLessonModal = useCallback(() => {
    setShowAddFromLessonModal(false);
  }, []);

  const addToLessonLibrary = useCallback(() => {
    hideAddUIOnOpen();
    setShowAddToLessonModal(true);
  }, []);

  const closeAddToLessonModal = useCallback(() => {
    setShowAddToLessonModal(false);
  }, []);

  const adminDropdownItems = useMemo(() => {
    const items: NvDropdownOption[] = [];

    // Back to Course Outline
    items.push({
      type: 'text',
      text: t.LECTURE_PAGES.ADMIN.EDIT_MENU_TOP_HEADER.BACK_TO_OUTLINE(courseAliases),
      callback: outlineHref,
      dataQa: config.pendo.lectureEdit.editMenuTopPanel.backToCourseOutlineHome,
    });

    // Duplicate Lesson
    items.push({
      type: 'text',
      text: t.LECTURE_PAGES.ADMIN.EDIT_MENU_TOP_HEADER.DUPLICATE_LESSON(courseAliases),
      callback: duplicateThisLecture,
      dataQa: config.pendo.lectureEdit.editMenuTopPanel.duplicateLessonOption,
    });

    items.push({ type: 'divider' });

    items.push(
      {
        type: 'text',
        text: t.LECTURE_PAGES.ADMIN.EDIT_MENU_TOP_HEADER.ADD_BLANK_LESSON(courseAliases),
        callback: createLesson,
        dataQa: config.pendo.lectureEdit.editMenuTopPanel.createLessonOption,
      },

    );

    if (!currentCourse?.isContentManagementCollection && canLinkCollectionLesson) {
      items.push(
        {
          type: 'text',
          text: t.LECTURE_PAGES.ADMIN.COG_OPTIONS.ADD_FROM_LESSON_LIBRARY(),
          callback: addFromLessonLibrary,
          dataQa: config.pendo.lectureEdit.editMenuTopPanel.addFromLessonLibraryOption,
        },
      );
    }

    if (!lecturePage?.isLinked && canLessonContentConvertibleToCollection?.[lecturePage?.id]) {
      items.push({ type: 'divider' });
      items.push(
        {
          type: 'text',
          text: t.TIMELINE.LESSON_OPTIONS.ADD_TO_COLLECTION(),
          callback: addToLessonLibrary,
          dataQa: 'add-to-lesson-library-option',
        },
      );
    }

    items.push({ type: 'divider' });

    // Delete Lesson
    items.push({
      type: 'text',
      text: t.LECTURE_PAGES.ADMIN.EDIT_MENU_TOP_HEADER.DELETE_LESSON(courseAliases),
      class: 'text-danger',
      callback: deleteThisLecture,
      dataQa: config.pendo.lectureEdit.editMenuTopPanel.deleteLessonOption,
    });

    return items;
  }, [courseAliases, outlineHref, duplicateThisLecture, createLesson,
    currentCourse?.isContentManagementCollection, canLinkCollectionLesson,
    lecturePage?.isLinked, lecturePage?.id, canLessonContentConvertibleToCollection,
    deleteThisLecture, addFromLessonLibrary, addToLessonLibrary]);

  const handleToggle = useCallback(
    (showList) => {
      setIsDropdownOpen(showList);
      if (showList) {
        hideAddUIOnOpen();
      }
    },
    [hideAddUIOnOpen],
  );

  return (
    <div css={styles}>
      <NvDropdown
        buttonStyle={NvDropdownButtonStyle.ICON}
        offset={10}
        minWidth={190}
        onToggle={handleToggle}
        ariaExpanded={isDropdownOpen}
        items={adminDropdownItems}
        iconClass='icon-more edit-menu-icon-styles icon-small d-flex text-white align-items-center justify-content-center mr-4'
        align={NvDropdownAlign.LEFT}
        toggleDataQa={config.pendo.lectureEdit.editMenuTopPanel.moreOptions}
        altLabel={t.LECTURE_PAGES.ADMIN.EDIT_MENU_TOP_HEADER.MORE_OPTIONS()}
        focusOutOnItemClick
      />

      {showAddFromLessonModal && (
        <LinkCollectionLessonsModal
          show={showAddFromLessonModal}
          onClose={closeAddFromLessonModal}
          sectionId={lecturePage?.lectureSectionId}
          courseId={currentCourse.id}
        />
      )}

      {showAddToLessonModal && (
        <AddToCollectionModal
          show={showAddToLessonModal}
          onClose={closeAddToLessonModal}
          lessonId={lecturePage?.id}
        />
      )}

    </div>
  );
};

export default MoreMenuActionButton;
